/// <amd-module name="Feed/Legacy/Protocols/PyPi/PyPiPackageProtocol" />
import * as React from "react";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { IVssIconProps } from "VSSUI/VssIcon";

import { FeedRole, FeedView, Package, PackageMetrics, PackageVersion, PackageVersionMetrics } from "Feed/Common/Contracts/Feed";
import { UpstreamingBehavior } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import * as PackageResources from "Feed/Common/Resources";
import { ImageIconProps } from "Feed/Common/Utils/Icons";
import { ProtocolCommands } from "Feed/Legacy/Common/ProtocolCommands";
import { isV2Feed } from "Feed/Legacy/Helpers/FeedCapabilityHelper";
import * as PermissionHelper from "Feed/Legacy/Helpers/PermissionHelper";
import { getVersionedContentUrl } from "Feed/Legacy/Helpers/UrlHelper";
import { BatchGenerator, IBatchIterator, TransformingBatchGenerator } from "Feed/Legacy/Protocols/Common/BatchHandling";
import { DeleteCommandHelper } from "Feed/Legacy/Protocols/Common/DeleteCommandHelper";
import { IPackageDetailsProps } from "Feed/Legacy/Protocols/Common/IPackageDetailsProps";
import { IPackageProtocol, IPackageProtocolClient, isPackage } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { ISupportedFeatures } from "Feed/Legacy/Protocols/Common/ISupportedFeatures";
import { PromotePackageCommand, PromotePackagesCommand } from "Feed/Legacy/Protocols/Common/PackageCommands";
import { MinimalPackageDetails } from "Feed/Legacy/Protocols/Common/WebApi/PackagingShared.Contracts";
import { PyPiClientTool } from "Feed/Legacy/Protocols/PyPi/PyPiClientTool";
import { IFeedPyPiDataService } from "Feed/Legacy/Protocols/PyPi/PyPiDataService";
import { PyPiOverviewAttributesPanel } from "Feed/Legacy/Protocols/PyPi/PyPiOverviewAttributesPanel";
import { PyPiOverviewContentPanel } from "Feed/Legacy/Protocols/PyPi/PyPiOverviewContentPanel";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export class PyPiPackageProtocol implements IPackageProtocol {
    public readonly name = "Python";
    public readonly key = "PyPI";
    private icon: IVssIconProps;

    // tslint:disable:no-bitwise
    public supportedCommandsMask: ProtocolCommands =
        ProtocolCommands.Promote |
        ProtocolCommands.Delete |
        ProtocolCommands.Restore |
        ProtocolCommands.PermanentDelete;
    public readonly clients: IPackageProtocolClient[] = [];

    private pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext) {
        this.pageContext = pageContext;
        this.clients.push(new PyPiClientTool(this.pageContext, this.getPublishCommand));
    }

    get vssIconProps() {
        if (!this.icon) {
            this.icon = new ImageIconProps(this.getBrandIconSourceUrl(), "brand-image-icon");
        }
        return this.icon;
    }

    get PyPiDataService(): IFeedPyPiDataService {
        return this.pageContext.getService<IFeedPyPiDataService>("IFeedPyPiDataService");
    }

    public getPackageMessage(packageSummary: Package, packageVersion: PackageVersion): string {
        if (packageVersion && packageVersion.isDeleted) {
            return Utils_String.format(
                PackageResources.PackageDeletedMessage,
                packageVersion.version,
                packageSummary.name
            );
        }

        return null;
    }

    public getOverviewAttributesPanel(
        feed: Feed,
        packageSummary: Package,
        packageVersion: PackageVersion,
        packageVersionMetrics: PackageVersionMetrics,
        packageMetrics: PackageMetrics,
        isSmartDependenciesEnabled: boolean,
        isProvenanceEnabled: boolean,
        isMetricsEnabled: boolean,
        isMetricsLoading: boolean
    ): React.ReactNode {
        return React.createElement(PyPiOverviewAttributesPanel, {
            pageContext: this.pageContext,
            feed,
            packageSummary,
            packageVersion,
            protocol: this,
            packageVersionMetrics,
            packageMetrics,
            isSmartDependenciesEnabled,
            isProvenanceEnabled,
            isMetricsEnabled,
            isMetricsLoading
        } as IPackageDetailsProps);
    }

    public getOverviewContentPanel(
        feed: Feed,
        feedViews: FeedView[],
        packageSummary: Package,
        packageVersion: PackageVersion
    ): React.ReactNode {
        return React.createElement(PyPiOverviewContentPanel, {
            pageContext: this.pageContext,
            feed,
            feedViews,
            packageSummary,
            packageVersion,
            protocol: this
        } as IPackageDetailsProps);
    }

    public getMultiSelectPackageCommands(
        feed: Feed,
        selectedPackages?: Package[],
        selectedVersions?: PackageVersion[]
    ): IPackageCommand[] {
        const packageCommands = new Array<IPackageCommand>();

        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(feed);
        const showDisabledCommands = role === FeedRole.None || role === FeedRole.Reader;

        if (selectedPackages && selectedPackages.length > 0) {
            // Promote
            // Only add Promote button on package list for v1
            packageCommands.push(new PromotePackagesCommand(selectedPackages, showDisabledCommands));
        }

        const deleteCommands = DeleteCommandHelper.addDeleteCommands(
            this.key,
            selectedPackages,
            selectedVersions,
            null /*packageName*/,
            !PermissionHelper.isAdministratorFromRole(role) /*disabled*/
        );
        packageCommands.push(...deleteCommands);

        return packageCommands;
    }

    // Get a list of available package commands for this protocol based on the user's role
    public getPackageCommands(
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean = false
    ): IPackageCommand[] {
        const packageCommands = new Array<IPackageCommand>();

        if (packageVersion.isDeleted) {
            return packageCommands;
        }

        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(feed);
        const showDisabledCommands = role === FeedRole.None || role === FeedRole.Reader;

        // Promote
        packageCommands.push(new PromotePackageCommand(packageSummary, viaPackageList, showDisabledCommands));

        // Delete
        const selectedPackages: Package[] = viaPackageList ? [packageSummary] : null;
        const selectedVersions: PackageVersion[] = viaPackageList ? null : [packageVersion];
        const deleteCommands = DeleteCommandHelper.addDeleteCommands(
            this.key,
            selectedPackages,
            selectedVersions,
            packageSummary.name,
            !PermissionHelper.isAdministratorFromRole(role) /*disabled*/
        );
        packageCommands.push(...deleteCommands);

        return packageCommands;
    }

    public supportedFeatures(feed?: Feed): ISupportedFeatures {
        const validCapability = feed ? isV2Feed(feed) : true;
        const features: ISupportedFeatures = {
            upstreams: validCapability,
            projectScopedFeeds: true,
            publicUpstreams: true
        };
        return features;
    }

    // Use supportedFeatures instead.
    // Except for connect to feed dialog that is located in the old Feed/Service/Web location.
    public supportsUpstreams(feed?: Feed): boolean {
        return feed ? isV2Feed(feed) : true;
    }

    public async deleteLatestVersions(
        projectId: string = null /*Project scoped feeds not yet enabled for this protocol */,
        feed: Feed,
        pkgs: Package[]
    ): Promise<void> {
        try {
            const iterator = new TransformingBatchGenerator(
                pkgs,
                this.PyPiDataService.batchSize,
                pkg =>
                    <MinimalPackageDetails>{
                        id: pkg.name,
                        version: pkg.versions[0].normalizedVersion
                    }
            );
            await this.PyPiDataService.batchDelete(feed, iterator);
        } catch (error) {
            throw new Error(this.key);
        }
    }

    public async deletePackageVersions(
        projectId: string = null /*Project scoped feeds not yet enabled for this protocol */,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ) {
        const iterator = new TransformingBatchGenerator(
            versions,
            this.PyPiDataService.batchSize,
            packageVersion =>
                <MinimalPackageDetails>{
                    id: pkg.name,
                    version: packageVersion.normalizedVersion
                }
        );
        await this.PyPiDataService.batchDelete(feed, iterator);
    }

    public async restorePackageVersionsToFeed(projectId: string, feed: Feed, pkg: Package, versions: PackageVersion[]) {
        const iterator = new TransformingBatchGenerator(
            versions,
            this.PyPiDataService.batchSize,
            packageVersion =>
                <MinimalPackageDetails>{
                    id: pkg.name,
                    version: packageVersion.normalizedVersion
                }
        );
        await this.PyPiDataService.batchRestoreToFeed(feed, iterator);
    }

    public async permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public async permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public async permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ) {
        let iterator: IBatchIterator<MinimalPackageDetails[]>;
        if (isPackage(pkgOrDetails)) {
            const pkgName = pkgOrDetails.name;
            iterator = new TransformingBatchGenerator(
                versions,
                this.PyPiDataService.batchSize,
                packageVersion => <MinimalPackageDetails>{ id: pkgName, version: packageVersion.normalizedVersion }
            );
        } else {
            iterator = new BatchGenerator(pkgOrDetails, this.PyPiDataService.batchSize);
        }

        await this.PyPiDataService.batchPermanentlyDelete(feed, iterator);
    }

    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public async promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ): Promise<void> {
        let iterator: IBatchIterator<MinimalPackageDetails[]>;
        if (isPackage(pkgOrDetails)) {
            const pkgName = pkgOrDetails.name;
            iterator = new TransformingBatchGenerator(
                versions,
                this.PyPiDataService.batchSize,
                packageVersion => <MinimalPackageDetails>{ id: pkgName, version: packageVersion.normalizedVersion }
            );
        } else {
            iterator = new BatchGenerator(pkgOrDetails, this.PyPiDataService.batchSize);
        }

        await this.PyPiDataService.batchPromote(feed, feedView.id, iterator);
    }

    public getCopyInstallCommand(
        feed: Feed,
        pkgName: string,
        pkgVersion: PackageVersion,
        separator: string = " "
    ): string {
        return `pip install ${pkgName}==${pkgVersion.version}`;
    }

    public getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior> {
        return this.PyPiDataService.getUpstreamingBehavior(feed, pkg);
    }

    public setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void> {
        return this.PyPiDataService.setUpstreamingBehavior(feed, pkg, behavior);
    }

    private getBrandIconSourceUrl(): string {
        return getVersionedContentUrl(this.pageContext, "ms.feed/brandcontent/icon-brand-python.svg");
    }

    private getPublishCommand(feedName: string): string {
        return Utils_String.format(PackageResources.PyPiPackageUploadCommand, feedName);
    }
}
